import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Mail, Globe, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PROJECT_TYPES, CONTACT } from "@/data/site";
import { useAuth } from "@/lib/AuthContext";

export default function Contact() {
  const location = useLocation();
  const queryType = new URLSearchParams(location.search).get("type") || "";
  const { user } = useAuth();

  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    project_type: queryType && PROJECT_TYPES.includes(queryType) ? queryType : "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (queryType && PROJECT_TYPES.includes(queryType)) {
      setForm((f) => ({ ...f, project_type: queryType }));
    }
  }, [queryType]);

  // Pre-fill name + email from authenticated user (only if fields are empty)
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
      }));
    }
  }, [user]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_type || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", {
        ...form,
        submission_kind:
          form.project_type === "Investor / Partnership Opportunity"
            ? "partnership"
            : form.project_type === "Domain Portfolio Inquiry"
            ? "offer"
            : "contact",
      });
      setSubmitted(true);
      toast.success("Message received. Our team will respond shortly.");
      setForm({ name: "", company: "", email: "", project_type: "", message: "" });
    } catch (err) {
      toast.error("Submission failed. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="pt-32 pb-32 md:pt-40 relative min-h-screen" data-testid="contact-page">
      <div className="absolute inset-0 grid-bg radial-mask opacity-40" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left */}
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">Contact Us</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900">
              Let's build something powerful.
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed">
              Contact S&amp;B Technologies to discuss software development, consulting, domain
              acquisitions, digital asset sales, investor opportunities, or partnership options.
            </p>

            <div className="mt-12 space-y-5">
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email"
                className="group flex items-center gap-4 p-5 border border-slate-200 hover:border-[#0066FF]/50 transition-colors"
              >
                <span className="flex h-11 w-11 items-center justify-center border border-[#0066FF]/40 text-[#0066FF]">
                  <Mail size={18} />
                </span>
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
                    Email
                  </div>
                  <div className="text-base text-slate-900 mt-1 group-hover:text-[#0066FF] transition-colors">
                    {CONTACT.email}
                  </div>
                </div>
              </a>
              <div className="flex items-center gap-4 p-5 border border-slate-200">
                <span className="flex h-11 w-11 items-center justify-center border border-[#0066FF]/40 text-[#0066FF]">
                  <Globe size={18} />
                </span>
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
                    Website
                  </div>
                  <div className="text-base text-slate-900 mt-1">{CONTACT.website}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <div className="tracing-card p-8 md:p-10">
              {submitted ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <div className="font-display text-2xl font-semibold text-slate-900">
                    Message received.
                  </div>
                  <p className="mt-3 text-sm text-slate-600 max-w-md mx-auto">
                    Thank you. A member of our team will respond to{" "}
                    <span className="text-[#0066FF]">{CONTACT.email}</span> shortly.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-6 btn-secondary"
                    data-testid="contact-send-another-btn"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="eyebrow">// Start the Conversation</div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">
                        Name *
                      </label>
                      <input
                        required
                        type="text"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        className="mt-2 w-full bg-white border border-slate-300 px-4 py-3 text-sm text-slate-900 focus-ring outline-none transition-colors"
                        data-testid="contact-input-name"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">
                        Company
                      </label>
                      <input
                        type="text"
                        value={form.company}
                        onChange={(e) => set("company", e.target.value)}
                        className="mt-2 w-full bg-white border border-slate-300 px-4 py-3 text-sm text-slate-900 focus-ring outline-none transition-colors"
                        data-testid="contact-input-company"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">
                      Email *
                    </label>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      className="mt-2 w-full bg-white border border-slate-300 px-4 py-3 text-sm text-slate-900 focus-ring outline-none transition-colors"
                      data-testid="contact-input-email"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">
                      Project Type *
                    </label>
                    <select
                      required
                      value={form.project_type}
                      onChange={(e) => set("project_type", e.target.value)}
                      className="mt-2 w-full bg-white border border-slate-300 px-4 py-3 text-sm text-slate-900 focus-ring outline-none transition-colors appearance-none cursor-pointer"
                      data-testid="contact-select-project-type"
                      style={{
                        backgroundImage:
                          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2300E5FF' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 1rem center",
                        paddingRight: "2.5rem",
                      }}
                    >
                      <option value="" disabled>
                        Select project type
                      </option>
                      {PROJECT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500">
                      Message *
                    </label>
                    <textarea
                      required
                      rows={6}
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      className="mt-2 w-full bg-white border border-slate-300 px-4 py-3 text-sm text-slate-900 focus-ring outline-none transition-colors resize-none"
                      placeholder="Tell us about your project, investment thesis, or inquiry..."
                      data-testid="contact-input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full justify-center disabled:opacity-50"
                    data-testid="contact-submit-btn"
                  >
                    {submitting ? "Sending..." : "Send Message"} <ArrowRight size={16} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
